import React, { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { ThemeProvider } from "@/context/ThemeContext";
import { Toaster } from "sonner";
import { consumeFirstVisitLang, LANGUAGES } from "@/i18n";
import Layout from "@/components/Layout";
import ProtectedRoute from "@/components/ProtectedRoute";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Dashboard from "@/pages/Dashboard";
import Students from "@/pages/Students";
import StudentProfile from "@/pages/StudentProfile";
import Teachers from "@/pages/Teachers";
import TeacherProfile from "@/pages/TeacherProfile";
import Courses from "@/pages/Courses";
import CourseDetail from "@/pages/CourseDetail";
import Classes from "@/pages/Classes";
import Attendance from "@/pages/Attendance";
import Payments from "@/pages/Payments";
import Announcements from "@/pages/Announcements";
import Messages from "@/pages/Messages";
import Portal from "@/pages/Portal";
import TeacherPortal from "@/pages/TeacherPortal";
import Reports from "@/pages/Reports";
import Settings from "@/pages/Settings";
import Placeholder from "@/pages/Placeholder";
import VerifyCertificate from "@/pages/VerifyCertificate";
import Users from "@/pages/Users";

function RoleHome() {
  const { user } = useAuth();
  if (user && (user.role === "parent" || user.role === "student")) return <Navigate to="/portal" replace />;
  if (user && user.role === "teacher") return <Navigate to="/teacher-portal" replace />;
  return <Dashboard />;
}

function FirstVisitWelcome() {
  const { t } = useTranslation();
  useEffect(() => {
    const code = consumeFirstVisitLang();
    if (!code) return;
    const lng = LANGUAGES.find((l) => l.code === code);
    if (!lng) return;
    // Delay slightly so the toaster is mounted
    const id = setTimeout(() => {
      toast.info(t("settings.welcomeAutoLang", { lang: lng.native }), { duration: 8000 });
    }, 800);
    return () => clearTimeout(id);
  }, [t]);
  return null;
}

export default function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <BrowserRouter>
          <Toaster position="top-right" richColors closeButton />
          <FirstVisitWelcome />
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/verify/:cert_no" element={<VerifyCertificate />} />
            <Route path="/" element={<ProtectedRoute><Layout /></ProtectedRoute>}>
              <Route index element={<RoleHome />} />
              <Route path="portal" element={<Portal />} />
              <Route path="teacher-portal" element={<TeacherPortal />} />
              <Route path="students" element={<Students />} />
              <Route path="students/:id" element={<StudentProfile />} />
              <Route path="teachers" element={<Teachers />} />
              <Route path="teachers/:id" element={<TeacherProfile />} />
              <Route path="courses" element={<Courses />} />
              <Route path="courses/:id" element={<CourseDetail />} />
              <Route path="classes" element={<Classes />} />
              <Route path="attendance" element={<Attendance />} />
              <Route path="payments" element={<Payments />} />
              <Route path="announcements" element={<Announcements />} />
              <Route path="messages" element={<Messages />} />
              <Route path="reports" element={<Reports />} />
              <Route path="users" element={<Users />} />
              <Route path="settings" element={<Settings />} />
            </Route>
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </ThemeProvider>
  );
}
